import "./SimulationStockChartManagerComponent.scss";
import {IComponentOptions, IOnDestroy, IOnInit, ITimeoutService} from "angular";
import {StockChartService} from "../service/StockChartService";
import {ChartIQDataPoint} from "../../../../components/chartiq/types/ChartIQFeed";
import {DateTimeUtil} from "../../../../js/util/DateTimeUtil";
import {ArrayUtil} from "../../../../js/util/ArrayUtil";
import {Pair} from "../../../../types/GenericTypes";
import {Optional} from "../../../../js/util/Optional";
import {Observable, ReplaySubject, Subject} from "rxjs";
import {Simulation} from "../../../models/Simulation";
import {SimulationChartData} from "../model/SimulationChartData";
import {TradeSimulatorViewStore} from "../../../service/TradeSimulatorViewStore";
import {
    TfChartDataDailySeries,
    TfChartDataPoint,
    TfChartDataSeries, TfDailyChartDataPoint
} from "../../../../components/chartiq/models/TfChartData";
import {IsoDate} from "../../../../pseudo-types/DateTypes";
import moment from "moment";

export const StockChartManager = "tfStockChartManager";

export function stockChartManager(): IComponentOptions {
    return {
        templateUrl: "app/trade-simulator/components/stock-chart/manager/SimulationStockChartManagerComponent.html",
        bindings: {
            viewStore: "<"
        },
        controllerAs: "ctrl",
        controller: SimulationStockChartManagerController
    };
}

const MAX_DATE_RANGE_MILLI = DateTimeUtil.ONE_YEAR_MILLIS * 2;

export class SimulationStockChartManagerController implements IOnInit, IOnDestroy {

    public viewStore: TradeSimulatorViewStore;

    public currentChartData: SimulationChartData | null;

    private chartDataCache = new Map<number, TfChartDataDailySeries>();
    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private stockChartService: StockChartService, private $timeout: ITimeoutService) {

    }

    public $onInit(): void {
        Observable.combineLatest(this.viewStore.currentSimulation, this.viewStore.currentDate)
                  .takeUntil(this.destroyed$)
                  .switchMap(([simulation, date]) => this.processChanges(simulation, date))
                  .subscribe((data) => this.$timeout(() => this.currentChartData = data));

        this.viewStore
            .simulations
            .takeUntil(this.destroyed$)
            .subscribe((simulations) => this.stockChartService.removeOrphanedSettings(simulations));
    }

    private async processChanges(simulation: Simulation | null,
                                 selectedDate: IsoDate | null): Promise<SimulationChartData | null> {
        if (simulation == null || selectedDate == null) {
            return null;
        } else {
            const startDate = this.rangeStartDate(simulation, selectedDate);
            const chartStartTime = moment(startDate).subtract(1, "year");
            const chartStartDate = DateTimeUtil.momentToIsoDate(chartStartTime);
            return this.updateChartData(simulation, chartStartDate, selectedDate);
        }
    }

    private rangeStartDate(simulation: Simulation, selectedDate: IsoDate): IsoDate {
        const minTradeDate = simulation.trades
                                       .map((t) => t.date)
                                       .sort(DateTimeUtil.sort)[0];
        return Optional.of(minTradeDate).orElse(selectedDate);
    }

    private async updateChartData(simulation: Simulation,
                                  startDate: IsoDate,
                                  endDate: IsoDate): Promise<SimulationChartData> {
        const simulationId = simulation.id;
        const code = simulation.underlying;

        let currentData = this.chartDataCache.get(simulation.id) || [];

        if (!this.hasAllDataForRange(currentData, startDate, endDate)) {
            currentData = await this.updateMainData(currentData, code, startDate, endDate);
        }

        const filtered = this.filterData(currentData, startDate, endDate);
        const newChartData = {
            simulationId,
            code,
            data: ArrayUtil.clone(filtered)
        };

        this.chartDataCache.set(simulation.id, currentData);
        return newChartData;
    }

    private hasAllDataForRange(currentData: TfChartDataDailySeries, startDate: IsoDate, endDate: IsoDate): boolean {
        if (currentData.length === 0) {
            return false;
        }
        const [earliest, mostRecent] = this.dataRange(currentData);

        const containsStartDate = earliest <= startDate;
        const containsEndDate = mostRecent >= endDate;
        return containsStartDate && containsEndDate;
    }

    private dataRange(data: TfChartDataDailySeries): Pair<IsoDate> {
        const current = Array.from(data).sort(this.sortPoints);
        const earliest = current[0];
        const mostRecent = current[current.length - 1];
        return [earliest.Date, mostRecent.Date];
    }

    private sortPoints(d1: TfDailyChartDataPoint, d2: TfDailyChartDataPoint): number {
        return d1.Date.localeCompare(d2.Date);
    }

    private filterData(data: TfChartDataDailySeries, startDate: IsoDate, endDate: IsoDate): TfChartDataDailySeries {
        const isBetween = (point: TfDailyChartDataPoint) => (point.Date > startDate) && (point.Date < endDate);
        return data.filter(isBetween);
    }

    private async updateMainData(currentData: TfChartDataDailySeries,
                                 code: string,
                                 startDate: IsoDate,
                                 endDate: IsoDate): Promise<TfChartDataDailySeries> {
        const currentDataSet = currentData;
        if (currentDataSet.length === 0) {
            const newData = await this.retrieveMissingData(code, startDate, endDate);
            return this.joinData(currentDataSet, newData);
        } else {
            const ranges = this.findMissingRange(currentDataSet, startDate, endDate);
            const requests = ranges.map(r => this.retrieveMissingData(code, r[0], r[1]));
            const newPoints = await Promise.all(requests);
            return this.joinData(currentDataSet, ...newPoints);
        }
    }

    private async retrieveMissingData(code: string,
                                      startDate: IsoDate,
                                      endDate: IsoDate): Promise<TfChartDataDailySeries> {
        const start = DateTimeUtil.createDate(startDate);
        const end = DateTimeUtil.createDate(endDate);
        return await this.stockChartService.request(code, start, end);
    }

    private joinData(initial: TfChartDataDailySeries, ...data: Array<TfChartDataDailySeries>): TfChartDataDailySeries {
        const allData = data.reduce((acc, next) => acc.concat(next), initial);
        const newDataSet = ArrayUtil.distinctBy(allData, (point) => point.Date);
        return newDataSet.sort(this.sortPoints);
    }

    private findMissingRange(data: TfChartDataDailySeries, startDate: IsoDate, endDate: IsoDate): Array<Pair<IsoDate>> {
        const [earliest, mostRecent] = this.dataRange(data);
        const startEarlier = startDate <= earliest;
        const endLater = endDate >= mostRecent;
        if (startEarlier && endLater) {
            const range1: Pair<IsoDate> = [startDate, earliest];
            const range2: Pair<IsoDate> = [mostRecent, endDate];
            return [range1, range2];
        } else if (startEarlier) {
            const range: Pair<IsoDate> = [startDate, earliest];
            return [range];
        } else {
            const range: Pair<IsoDate> = [mostRecent, endDate];
            return [range];
        }
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
